import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";
import { isReaction } from "@/shared/lib/feedbackReaction";
import type { Reaction } from "@/shared/lib/feedbackReaction";
import type { ChatTurnFeedback } from "@/features/chat/model/chatTypes";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function toChatTurnFeedback(data: unknown): ChatTurnFeedback {
  if (!isRecord(data)) {
    throw new Error("Unexpected response shape.");
  }
  return {
    // `reaction` comes back null when the click toggled the reaction off.
    reaction: isReaction(data.reaction) ? data.reaction : null,
    comment: typeof data.comment === "string" ? data.comment : null,
  };
}

/** Records the reaction immediately — independent of whether a comment is ever submitted.
 * Sending the reaction the user already has clears it, unless a comment is attached. */
export async function setChatTurnReaction(
  turnId: string,
  reaction: Reaction,
): Promise<ChatTurnFeedback> {
  const res = await apiFetch(`/v1/chat/turns/${turnId}/feedback`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reaction }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return toChatTurnFeedback(await res.json());
}

/** Attaches text to the reaction already recorded, replacing any previous comment. */
export async function addChatTurnFeedbackComment(
  turnId: string,
  comment: string,
): Promise<ChatTurnFeedback> {
  const res = await apiFetch(`/v1/chat/turns/${turnId}/feedback`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comment }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return toChatTurnFeedback(await res.json());
}

/** Removes the reaction. The backend rejects this with 409 while a comment is attached. */
export async function clearChatTurnReaction(turnId: string): Promise<void> {
  const res = await apiFetch(`/v1/chat/turns/${turnId}/feedback`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
